import { useMemo } from 'react'
import {
  calculateExpectancyFromTrades,
  estimateExpectancyFromRatio,
} from '../common/utils/rMultipleCalculations'
import { MonteCarloResponse } from '../types/monteCarlo'
import { AttachMoney, Shield, Balance, EmojiEvents } from '@mui/icons-material'
import React from 'react'

export interface RobustnessMetrics {
  p05ProfitPercent: number
  p95MaxDDPercent: number
  riskOfRuinPercent: number
  finalEquityIQR: number
}

export interface RankingData {
  expectancy: number
  robustnessScore: number | RobustnessMetrics
  riskAdjustedReturn: number
}

export enum RankingCategory {
  EXPECTANCY = 'expectancy',
  ROBUST = 'robust',
  RISK_ADJUSTED = 'riskAdjusted',
}

export interface RankingResult {
  category: RankingCategory
  group: string
  value: number
  badge: string
  description: string
}

export interface SummaryWithRankings {
  [key: string]: any
  rankings: RankingResult[]
}

export interface CategoryConfig {
  icon: React.ReactElement
  color: string
}

const getCategoryIcon = (category: string): React.ReactElement => {
  switch (category) {
    case 'expectancy':
      return React.createElement(AttachMoney, { sx: { fontSize: 20 } })
    case 'robust':
      return React.createElement(Shield, { sx: { fontSize: 20 } })
    case 'riskAdjusted':
      return React.createElement(Balance, { sx: { fontSize: 20 } })
    default:
      return React.createElement(EmojiEvents, { sx: { fontSize: 20 } })
  }
}

const getCategoryColor = (category: string, theme?: any): string => {
  switch (category) {
    case 'expectancy':
      return '#9C27B0' // Purple
    case 'robust':
      return '#FF9800' // Orange
    case 'riskAdjusted':
      return '#2196F3' // Blue
    default:
      return theme?.palette?.primary?.main || '#1976d2'
  }
}

export const getCategoryConfig = (category: string, theme?: any): CategoryConfig => {
  return {
    icon: getCategoryIcon(category),
    color: getCategoryColor(category, theme),
  }
}

export function useBacktestRankings(
  backtest: any,
  tradesByGroup?: { [group: string]: any[] },
  monteCarloResults?: { [group: string]: MonteCarloResponse }
) {
  return useMemo(() => {
    if (!backtest?.summaries) return { summariesWithRankings: [], rankingResults: [] }

    const validSummaries = backtest.summaries.filter((summary: any) => summary.closedTradeCount > 0)

    if (validSummaries.length === 0) return { summariesWithRankings: [], rankingResults: [] }

    // Calculate ranking data for each summary
    const summariesWithRankingData = validSummaries.map((summary: any) => {
      // Calculate Expectancy
      let expectancy = 0
      if (summary.closedTradeCount > 0) {
        if (tradesByGroup && tradesByGroup[summary.group]) {
          const trades = tradesByGroup[summary.group]
          expectancy = calculateExpectancyFromTrades(trades)
        } else {
          // Fallback: estimate from ratio string (e.g., "1/6" → winners = +6R, losers = -1R)
          expectancy = estimateExpectancyFromRatio(
            summary.group,
            summary.profitTradeCount,
            summary.closedTradeCount
          )
        }
      }

      // Calculate Robustness Score using Monte-Carlo metrics
      const monteCarloData = monteCarloResults?.[summary.group]
      let robustnessScore: number | RobustnessMetrics = 0

      if (monteCarloData?.aggregates) {
        // Extract the four Monte-Carlo metrics
        const p05ProfitPercent = monteCarloData.aggregates.finals?.quantiles?.['0.05'] || 0
        const p95MaxDDPercent = monteCarloData.aggregates.maxDrawdown?.quantiles?.['0.95'] || 0
        const riskOfRuinPercent = monteCarloData.aggregates.riskOfRuin?.probability || 0
        const finalEquityIQR =
          (monteCarloData.aggregates.finals?.quantiles?.['0.75'] || 0) -
          (monteCarloData.aggregates.finals?.quantiles?.['0.25'] || 0)

        // Store raw metrics for normalization
        robustnessScore = {
          p05ProfitPercent,
          p95MaxDDPercent,
          riskOfRuinPercent,
          finalEquityIQR,
        } as RobustnessMetrics
      } else {
        // Fallback to old drawdown metric if no Monte-Carlo data
        robustnessScore = summary.absoluteDrawdownPercentage || 0
      }

      // Calculate Risk-Adjusted Return (MAR-like)
      const profitPercentage = summary.profitPercentage || 0
      const relativeDrawdown = summary.maxRelativeDrawdownPercentage || 0

      let riskAdjustedReturn
      if (relativeDrawdown > 0) {
        riskAdjustedReturn = profitPercentage / relativeDrawdown
      } else if (profitPercentage > 0) {
        riskAdjustedReturn = profitPercentage * 1000
      } else {
        riskAdjustedReturn = 0
      }

      return {
        ...summary,
        rankingData: {
          expectancy,
          robustnessScore,
          riskAdjustedReturn,
        },
      }
    })

    // Calculate normalized robustness scores
    const summariesWithMonteCarlo = summariesWithRankingData.filter(
      (summary: any) => typeof summary.rankingData.robustnessScore === 'object'
    )

    if (summariesWithMonteCarlo.length > 0) {
      // Extract all metrics for normalization
      const p05Profits = summariesWithMonteCarlo.map(
        (s: any) => (s.rankingData.robustnessScore as RobustnessMetrics).p05ProfitPercent
      )
      const p95MaxDDs = summariesWithMonteCarlo.map(
        (s: any) => (s.rankingData.robustnessScore as RobustnessMetrics).p95MaxDDPercent
      )
      const riskOfRuins = summariesWithMonteCarlo.map(
        (s: any) => (s.rankingData.robustnessScore as RobustnessMetrics).riskOfRuinPercent
      )
      const finalEquityIQRs = summariesWithMonteCarlo.map(
        (s: any) => (s.rankingData.robustnessScore as RobustnessMetrics).finalEquityIQR
      )

      // Calculate min-max for normalization
      const p05Min = Math.min(...p05Profits)
      const p05Max = Math.max(...p05Profits)
      const p95Min = Math.min(...p95MaxDDs)
      const p95Max = Math.max(...p95MaxDDs)
      const ruinMin = Math.min(...riskOfRuins)
      const ruinMax = Math.max(...riskOfRuins)
      const iqrMin = Math.min(...finalEquityIQRs)
      const iqrMax = Math.max(...finalEquityIQRs)

      // Normalize and calculate weighted robustness score
      summariesWithMonteCarlo.forEach((summary: any) => {
        const raw = summary.rankingData.robustnessScore as RobustnessMetrics

        // Normalize each metric (0-1 scale)
        const normP05Profit =
          p05Max !== p05Min ? (raw.p05ProfitPercent - p05Min) / (p05Max - p05Min) : 0.5
        const normP95MaxDD =
          p95Max !== p95Min ? 1 - (raw.p95MaxDDPercent - p95Min) / (p95Max - p95Min) : 0.5 // Inverted (lower is better)
        const normRiskOfRuin =
          ruinMax !== ruinMin ? 1 - (raw.riskOfRuinPercent - ruinMin) / (ruinMax - ruinMin) : 0.5 // Inverted (lower is better)
        const normFinalEquityIQR =
          iqrMax !== iqrMin ? 1 - (raw.finalEquityIQR - iqrMin) / (iqrMax - iqrMin) : 0.5 // Inverted (lower is better)

        // Calculate weighted robustness score (0-1)
        const weightedScore =
          normP05Profit * 0.4 + // p05 Profit% (higher is better)
          normP95MaxDD * 0.3 + // p95 MaxDD% (lower is better)
          normRiskOfRuin * 0.2 + // Risk of Ruin % (lower is better)
          normFinalEquityIQR * 0.1 // Final-equity IQR % (lower is better)

        summary.rankingData.robustnessScore = weightedScore
      })
    }

    // Find the best in each category
    const bestExpectancy = summariesWithRankingData.sort(
      (a: any, b: any) => b.rankingData.expectancy - a.rankingData.expectancy
    )[0]

    const mostRobust = summariesWithRankingData.sort(
      (a: any, b: any) => b.rankingData.robustnessScore - a.rankingData.robustnessScore
    )[0]

    const bestRiskAdjusted = summariesWithRankingData.sort(
      (a: any, b: any) => b.rankingData.riskAdjustedReturn - a.rankingData.riskAdjustedReturn
    )[0]

    // Create ranking results
    const rankingResults: RankingResult[] = [
      {
        category: RankingCategory.EXPECTANCY,
        group: bestExpectancy?.group || '',
        value: bestExpectancy?.rankingData.expectancy || 0,
        badge: 'Best Expectancy',
        description: 'Highest average R/trade',
      },
      {
        category: RankingCategory.ROBUST,
        group: Object.keys(monteCarloResults || {}).length > 0 ? mostRobust?.group || '' : null,
        value:
          Object.keys(monteCarloResults || {}).length > 0
            ? mostRobust?.rankingData.robustnessScore * 100 || 0
            : 0,
        badge: 'Most Robust',
        description: 'Best Monte-Carlo robustness score',
      },
      {
        category: RankingCategory.RISK_ADJUSTED,
        group: bestRiskAdjusted?.group || '',
        value: bestRiskAdjusted?.rankingData.riskAdjustedReturn || 0,
        badge: 'Best Risk-Adjusted Return',
        description: 'Best MAR ratio',
      },
    ]

    // Add rankings to each summary
    const summariesWithRankings: SummaryWithRankings[] = summariesWithRankingData.map(
      (summary: any) => {
        const rankings = rankingResults
          .filter((result) => result.group === summary.group)
          .map((result) => ({
            category: result.category,
            value: result.value,
            badge: result.badge,
            description: result.description,
          }))

        return {
          ...summary,
          rankings,
        }
      }
    )

    return {
      summariesWithRankings,
      rankingResults,
    }
  }, [backtest?.summaries, tradesByGroup, monteCarloResults])
}
